import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Form,
  Alert,
  Spinner,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";
import { useAuth } from "../../contexts/AuthContext";
import { fires, auth } from "../../firebase";
import firebase from "firebase";
import { useNavigate } from "react-router-dom";
import { FaEnvelope, FaCalendarWeek, FaCheckCircle, FaTimesCircle, FaCalendar, FaMapMarkerAlt, FaGlobe } from "react-icons/fa";

export default function AdminWeeklyEmailCampaign() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignTitle, setCampaignTitle] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState({ type: "", message: "" });
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateResult, setTemplateResult] = useState({ type: "", message: "" });
  const [emailTemplate, setEmailTemplate] = useState({
    name: "Weekly Email Campaign",
    subject: "",
    htmlContent: "",
  });

  useEffect(() => {
    checkAdminAccess();
  }, [currentUser]);

  useEffect(() => {
    if (isAdmin) {
      loadCampaigns();
      loadFeaturedEvents();
      loadWeeklyEmailTemplate();
    }
  }, [isAdmin]);

  // Reload campaigns after creating a new one
  useEffect(() => {
    if (success && isAdmin) {
      loadCampaigns();
    }
  }, [success, isAdmin]);

  const checkAdminAccess = async () => {
    try {
      if (!currentUser) {
        setError("Please log in to access this page");
        setLoading(false);
        return;
      }

      const adminDoc = await fires.collection("admin").doc(currentUser.uid).get();
      if (!adminDoc.exists) {
        setError("Access denied. Admin privileges required.");
        setLoading(false);
        return;
      }

      const adminData = adminDoc.data();
      if (!adminData.roles || !adminData.roles.includes("management")) {
        setError("Access denied. Management role required.");
        setLoading(false);
        return;
      }

      setIsAdmin(true);
      setLoading(false);
    } catch (err) {
      console.error("Error checking admin access:", err);
      setError("Error verifying admin access");
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      setLoadingCampaigns(true);
      // Load campaigns from Firestore campaigns collection, sorted by timestamp
      const campaignsSnapshot = await fires
        .collection("campaigns")
        .orderBy("timestamp", "desc")
        .limit(50)
        .get();

      const campaignsData = campaignsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setCampaigns(campaignsData);
    } catch (err) {
      console.error("Error loading campaigns:", err);
      // If timestamp field doesn't exist, try createdAt
      try {
        const campaignsSnapshot = await fires
          .collection("campaigns")
          .orderBy("createdAt", "desc")
          .limit(50)
          .get();

        const campaignsData = campaignsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setCampaigns(campaignsData);
      } catch (fallbackErr) {
        console.error("Error loading campaigns with fallback:", fallbackErr);
        // Try loading without orderBy as last resort
        try {
          const allCampaignsSnapshot = await fires
            .collection("campaigns")
            .limit(50)
            .get();

          const campaignsData = allCampaignsSnapshot.docs
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            .sort((a, b) => {
              const aTime = a.timestamp?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
              const bTime = b.timestamp?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
              return bTime - aTime; // Descending
            });

          setCampaigns(campaignsData);
        } catch (finalErr) {
          console.error("Error loading campaigns:", finalErr);
        }
      }
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const loadFeaturedEvents = async () => {
    try {
      setLoadingEvents(true);
      const now = firebase.firestore.Timestamp.now();
      
      // Load events where approved=true, featured=true, and startDate > now (upcoming events)
      try {
        const eventsSnapshot = await fires
          .collection("events")
          .where("approved", "==", true)
          .where("featured", "==", true)
          .where("startDate", ">", now)
          .orderBy("startDate", "asc")
          .limit(20)
          .get();

        const eventsData = eventsSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title || data.name || "Untitled Event",
            image: data.photoURL || data.image || data.photo || null,
            startDateString: data.startDateString || 
                            (data.startDate?.toDate?.()?.toLocaleDateString()) || 
                            "No date",
            location: data.address?.fullAddress || data.location || "No location",
            website: data.website || data.websiteURL || data.eventURL || null,
          };
        });

        setFeaturedEvents(eventsData);
      } catch (queryErr) {
        // If query fails (maybe index doesn't exist), try loading all and filtering
        console.log("Query failed, using fallback filter:", queryErr);
        const allEventsSnapshot = await fires
          .collection("events")
          .where("approved", "==", true)
          .where("featured", "==", true)
          .orderBy("timestamp", "desc")
          .limit(100)
          .get();

        const eventsData = allEventsSnapshot.docs
          .filter((doc) => {
            const data = doc.data();
            // Check if startDate exists and is greater than now
            if (!data.startDate) return false;
            const startDate = data.startDate?.toDate ? data.startDate.toDate() : 
                             (data.startDate?.toMillis ? new Date(data.startDate.toMillis()) : null);
            if (!startDate) return false;
            return startDate > new Date();
          })
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              title: data.title || data.name || "Untitled Event",
              image: data.photoURL || data.image || data.photo || null,
              startDateString: data.startDateString || 
                              (data.startDate?.toDate?.()?.toLocaleDateString()) || 
                              "No date",
              location: data.address?.fullAddress || data.location || "No location",
              website: data.website || data.websiteURL || data.eventURL || null,
            };
          });

        setFeaturedEvents(eventsData);
      }
    } catch (err) {
      console.error("Error loading featured events:", err);
      setFeaturedEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadRecipients = async () => {
    try {
      // Load all EventGo users or specific segments
      const usersSnapshot = await fires
        .collection("users")
        .where("email", "!=", null)
        .limit(1000)
        .get();

      const emails = [];
      usersSnapshot.docs.forEach((doc) => {
        const userData = doc.data();
        if (userData.email) {
          emails.push(userData.email);
        }
      });

      return emails;
    } catch (err) {
      console.error("Error loading recipients:", err);
      setError("Error loading recipients");
      return [];
    }
  };

  const sendWeeklyCampaign = async () => {
    if (!campaignTitle.trim()) {
      setError("Please enter a campaign title");
      return;
    }

    try {
      setSending(true);
      setError("");
      setSuccess("");

      // Call API to create campaign
      const token = await auth.currentUser.getIdToken();
      const apiUrl = `https://v2createemailcampaign-650318705789.us-central1.run.app/?mode=create&chunk_id=A&campaign_id=A&title=${encodeURIComponent(campaignTitle.trim())}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success || data.status === "success") {
        setSuccess("Weekly email campaign created successfully!");
        setCampaignTitle("");
        setShowCreateModal(false);
        // Reload campaigns to show the new one
        loadCampaigns();
      } else {
        setError("Failed to create campaign. Please try again.");
      }
    } catch (err) {
      console.error("Error creating campaign:", err);
      setError("Error creating campaign: " + err.message);
    } finally {
      setSending(false);
    }
  };

  const sendTestEmail = async () => {
    try {
      const email = (testEmail || "").trim();
      if (!email) {
        setTestEmailResult({ type: "danger", message: "Please enter an email address." });
        return;
      }
      if (!currentUser?.uid) {
        setTestEmailResult({ type: "danger", message: "Missing admin user id. Please re-login." });
        return;
      }

      setSendingTestEmail(true);
      setTestEmailResult({ type: "", message: "" });

      const response = await fetch(
        "https://v2serviceapitestemailcampaign-650318705789.us-central1.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            admin_uid: currentUser.uid,
            email,
          }),
        }
      );

      let data = null;
      try {
        data = await response.json();
      } catch (_) {
        data = null;
      }

      if (!response.ok) {
        throw new Error(data?.message || `Request failed (${response.status})`);
      }

      setTestEmailResult({
        type: "success",
        message: data?.message || "Test email request sent successfully.",
      });
    } catch (err) {
      setTestEmailResult({
        type: "danger",
        message: err?.message || "Failed to send test email.",
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const getDefaultWeeklyEmailTemplate = () => {
    return `
<div style="font-family: Arial, sans-serif; color: #222; line-height: 1.6;">
  <h2 style="margin: 0 0 16px;">This Week on EventGo</h2>
  <p>Hi there,</p>
  <p>Here are featured events we think you will enjoy this week.</p>
  <p>{{featuredEvents}}</p>
  <p style="margin-top: 24px;">See you out there,<br/>The EventGo Team</p>
</div>`.trim();
  };

  const loadWeeklyEmailTemplate = async () => {
    try {
      setTemplateLoading(true);
      const templateDoc = await fires
        .collection("emailTemplates")
        .doc("weeklyEmailCampaign")
        .get();

      if (templateDoc.exists) {
        const data = templateDoc.data();
        setEmailTemplate({
          name: data.name || "Weekly Email Campaign",
          subject: data.subject || "",
          htmlContent: data.htmlContent || data.content || "",
        });
      } else {
        setEmailTemplate({
          name: "Weekly Email Campaign",
          subject: "This Week on EventGo",
          htmlContent: getDefaultWeeklyEmailTemplate(),
        });
      }
    } catch (err) {
      console.error("Error loading weekly email template:", err);
      setTemplateResult({
        type: "danger",
        message: "Failed to load email template: " + err.message,
      });
    } finally {
      setTemplateLoading(false);
    }
  };

  const saveWeeklyEmailTemplate = async () => {
    const subject = emailTemplate.subject.trim();
    const htmlContent = emailTemplate.htmlContent.trim();

    if (!subject) {
      setTemplateResult({ type: "danger", message: "Please enter a template subject." });
      return;
    }

    if (!htmlContent) {
      setTemplateResult({ type: "danger", message: "Please enter template content." });
      return;
    }

    try {
      setTemplateSaving(true);
      setTemplateResult({ type: "", message: "" });

      await fires.collection("emailTemplates").doc("weeklyEmailCampaign").set(
        {
          name: emailTemplate.name || "Weekly Email Campaign",
          subject,
          htmlContent,
          content: htmlContent,
          type: "weeklyEmailCampaign",
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedBy: currentUser?.uid || null,
        },
        { merge: true }
      );

      setTemplateResult({
        type: "success",
        message: "Weekly email template saved successfully.",
      });
    } catch (err) {
      console.error("Error saving weekly email template:", err);
      setTemplateResult({
        type: "danger",
        message: "Failed to save email template: " + err.message,
      });
    } finally {
      setTemplateSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>{error || "You do not have permission to access this page."}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <FaCalendarWeek className="me-2" />
                Weekly Email Campaign Sender (For EventGo)
              </h2>
              <p className="text-muted mb-0">
                Send weekly email campaigns to EventGo users
              </p>
            </div>
            <div>
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
                className="d-flex align-items-center"
              >
                <FaEnvelope className="me-2" />
                Create Campaign
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Row>
        {/* Campaigns List - Left Side */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-secondary text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <FaEnvelope className="me-2" />
                Campaigns List
              </h5>
              <Button
                variant="light"
                size="sm"
                onClick={() => setShowCreateModal(true)}
              >
                <FaEnvelope className="me-1" />
                Create
              </Button>
            </Card.Header>
            <Card.Body>
              {loadingCampaigns ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <p className="text-muted mt-2 mb-0">Loading campaigns...</p>
                </div>
              ) : campaigns.length === 0 ? (
                <p className="text-muted text-center py-3">
                  No campaigns found
                </p>
              ) : (
                <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                  {campaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      className="mb-3 p-3 border rounded"
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/admin-weekly-email-campaign-view/${campaign.id}`)}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <strong className="text-truncate" style={{ maxWidth: "70%" }}>
                          {campaign.subject || campaign.title || "Untitled Campaign"}
                        </strong>
                        <Badge
                          bg={
                            campaign.status === "sent"
                              ? "success"
                              : campaign.status === "queued"
                              ? "warning"
                              : campaign.status === "completed"
                              ? "info"
                              : "danger"
                          }
                        >
                          {campaign.status || "unknown"}
                        </Badge>
                      </div>
                      <small className="text-muted d-block">
                        <strong>ID:</strong> {campaign.id}
                      </small>
                      <small className="text-muted d-block">
                        {campaign.emailCount || campaign.recipientCount || 0} recipients
                      </small>
                      <small className="text-muted d-block">
                        {campaign.timestamp?.toDate
                          ? campaign.timestamp.toDate().toLocaleDateString()
                          : campaign.createdAt?.toDate
                          ? campaign.createdAt.toDate().toLocaleDateString()
                          : campaign.timestamp?.toMillis
                          ? new Date(campaign.timestamp.toMillis()).toLocaleDateString()
                          : "N/A"}
                      </small>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Test Email + Featured Events */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <FaEnvelope className="me-2" />
                Test Email
              </h5>
            </Card.Header>
            <Card.Body>
              {testEmailResult.message ? (
                <Alert
                  variant={testEmailResult.type || "info"}
                  dismissible
                  onClose={() => setTestEmailResult({ type: "", message: "" })}
                  className="py-2"
                >
                  {testEmailResult.message}
                </Alert>
              ) : null}

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="name@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  disabled={sendingTestEmail}
                />
              </Form.Group>

              <Button
                variant="primary"
                onClick={sendTestEmail}
                disabled={sendingTestEmail}
                className="w-100 d-flex align-items-center justify-content-center"
              >
                {sendingTestEmail ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Sending...
                  </>
                ) : (
                  "Send Test Email"
                )}
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => navigate("/admin-mailersend-template-editor")}
                className="w-100 d-flex align-items-center justify-content-center mt-2"
              >
                Modify MailerSend Template
              </Button>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <FaEnvelope className="me-2" />
                Modify Email Template
              </h5>
              <Button
                variant="light"
                size="sm"
                onClick={() => navigate("/admin-mailersend-template-editor")}
              >
                Open Editor
              </Button>
            </Card.Header>
            <Card.Body>
              <Button
                variant="outline-primary"
                onClick={() => navigate("/admin-mailersend-template-editor")}
                className="w-100 d-flex align-items-center justify-content-center mb-3"
              >
                Modify with MailerSend Template Editor
              </Button>

              {templateResult.message ? (
                <Alert
                  variant={templateResult.type || "info"}
                  dismissible
                  onClose={() => setTemplateResult({ type: "", message: "" })}
                  className="py-2"
                >
                  {templateResult.message}
                </Alert>
              ) : null}

              {templateLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <p className="text-muted mt-2 mb-0">Loading template...</p>
                </div>
              ) : (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Template Subject</Form.Label>
                    <Form.Control
                      type="text"
                      value={emailTemplate.subject}
                      onChange={(e) =>
                        setEmailTemplate((prev) => ({
                          ...prev,
                          subject: e.target.value,
                        }))
                      }
                      disabled={templateSaving}
                      placeholder="This Week on EventGo"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Template Content</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={10}
                      value={emailTemplate.htmlContent}
                      onChange={(e) =>
                        setEmailTemplate((prev) => ({
                          ...prev,
                          htmlContent: e.target.value,
                        }))
                      }
                      disabled={templateSaving}
                      placeholder="Enter weekly campaign email HTML/content"
                      style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                    />
                  </Form.Group>

                  <Button
                    variant="primary"
                    onClick={saveWeeklyEmailTemplate}
                    disabled={
                      templateSaving ||
                      !emailTemplate.subject.trim() ||
                      !emailTemplate.htmlContent.trim()
                    }
                    className="w-100 d-flex align-items-center justify-content-center"
                  >
                    {templateSaving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      "Save Template"
                    )}
                  </Button>

                  <details className="mt-3">
                    <summary className="text-muted" style={{ cursor: "pointer" }}>
                      Preview template
                    </summary>
                    <div
                      className="border rounded p-3 mt-2"
                      style={{ maxHeight: "320px", overflowY: "auto" }}
                      dangerouslySetInnerHTML={{
                        __html: emailTemplate.htmlContent || "No content",
                      }}
                    />
                  </details>
                </Form>
              )}
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">
                <FaCalendarWeek className="me-2" />
                Featured Events
              </h5>
            </Card.Header>
            <Card.Body>
              {loadingEvents ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <p className="text-muted mt-2 mb-0">Loading events...</p>
                </div>
              ) : featuredEvents.length === 0 ? (
                <p className="text-muted text-center py-3">
                  No featured events found
                </p>
              ) : (
                <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                  {featuredEvents.map((event) => (
                    <div
                      key={event.id}
                      className="mb-3 p-3 border rounded"
                      style={{ cursor: "pointer" }}
                    >
                      {event.image && (
                        <img
                          src={event.image}
                          alt={event.title}
                          className="img-fluid rounded mb-2"
                          style={{
                            width: "100%",
                            height: "150px",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                      <h6 className="fw-bold mb-2" style={{ fontSize: "0.95rem" }}>
                        {event.title}
                      </h6>
                      <div className="mb-2">
                        <small className="text-muted d-block">
                          <i className="fas fa-calendar me-1"></i>
                          {event.startDateString}
                        </small>
                        <small className="text-muted d-block mt-1">
                          <i className="fas fa-map-marker-alt me-1"></i>
                          {event.location}
                        </small>
                        <small className="text-muted d-block mt-1">
                          <i className="fas fa-globe me-1"></i>
                          <a 
                            href={`https://eventgo.ai/event/${event.id}?source=campaign&campaignId={{campaignId}}&email={{email}}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none"
                            onClick={(e) => e.stopPropagation()}
                          >
                            https://eventgo.ai/event/{event.id}?source=campaign&campaignId={'{{campaignId}}'}&email={'{{email}}'}
                          </a>
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Campaign Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <FaEnvelope className="me-2" />
            Create Weekly Campaign
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Campaign Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter campaign title"
                value={campaignTitle}
                onChange={(e) => setCampaignTitle(e.target.value)}
                disabled={sending}
              />
              <Form.Text className="text-muted">
                Enter a title for this campaign.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreateModal(false);
              setCampaignTitle("");
            }}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={sendWeeklyCampaign}
            disabled={sending || !campaignTitle.trim()}
          >
            {sending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <FaEnvelope className="me-2" />
                Create Campaign
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Campaign Details Modal */}
      {selectedCampaign && (
        <Card className="border-0 shadow-sm mt-4">
          <Card.Header className="bg-info text-white">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Campaign Details</h5>
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setSelectedCampaign(null)}
              >
                <FaTimesCircle />
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <p>
                  <strong>Subject:</strong> {selectedCampaign.subject}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  <Badge
                    bg={
                      selectedCampaign.status === "sent"
                        ? "success"
                        : selectedCampaign.status === "queued"
                        ? "warning"
                        : "danger"
                    }
                  >
                    {selectedCampaign.status}
                  </Badge>
                </p>
                <p>
                  <strong>Recipients:</strong> {selectedCampaign.recipientCount || 0}
                </p>
              </Col>
              <Col md={6}>
                <p>
                  <strong>Created:</strong>{" "}
                  {selectedCampaign.createdAt?.toDate
                    ? selectedCampaign.createdAt.toDate().toLocaleString()
                    : "N/A"}
                </p>
                {selectedCampaign.sentAt && (
                  <p>
                    <strong>Sent:</strong>{" "}
                    {selectedCampaign.sentAt.toDate
                      ? selectedCampaign.sentAt.toDate().toLocaleString()
                      : "N/A"}
                  </p>
                )}
              </Col>
            </Row>
            <div className="mt-3">
              <strong>Content:</strong>
              <div
                className="border rounded p-3 mt-2"
                style={{ maxHeight: "300px", overflowY: "auto" }}
                dangerouslySetInnerHTML={{
                  __html: selectedCampaign.content || "No content",
                }}
              />
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
